import React, { useRef, useMemo, useState } from "react";

import useLineChart from "./hooks/useLineChart/useLineChart";
import ChartData, { Dataset } from "../types/ChartData";

import Card from "../../common/Card/Card";
import Typography from "../../common/Typography/Typography";
import Legend from "../Legend/Legend";
import CardActions from "../../common/Card/CardActions";

import styles from "./LineChart.module.css";

interface Props {
    title: string;
    data: ChartData;
    showLegend?: boolean;
}

const LineChart = ({ title, data, showLegend = true }: Props) => {
    // Array of data to be hidden in the line chart.
    const [hiddenData, setHiddenData] = useState<Dataset["label"][]>([]);

    // Create legend data from the chart data.
    const legend = useMemo(
        () =>
            data.datasets.map(({ label, backgroundColor }) => ({
                label,
                backgroundColor,
                hidden: hiddenData.includes(label),
            })),
        [data, hiddenData]
    );

    // Add and remove selected data from the hidden data array.
    const toggleLegend = (label: Dataset["label"]) => {
        if (hiddenData.includes(label)) {
            return setHiddenData((state) => state.filter((hiddenLabel) => hiddenLabel !== label));
        }

        return setHiddenData((state) => [...state, label]);
    };

    // Filter out hidden data from the chart data.
    const lineChartData = useMemo(
        () => ({
            ...data,
            datasets: data.datasets.filter(({ label }) => !hiddenData.includes(label)),
        }),
        [data, hiddenData]
    );

    // Render line chart and set it;s min width.
    const canvasRef = useRef(null);
    useLineChart(canvasRef, lineChartData);
    const minWidth = data.labels.length * 80;

    return (
        <Card padding="large" className="space-y-2  whitespace-nowrap">
            {title ? (
                <Typography as="h3" variant="heading">
                    {title}
                </Typography>
            ) : null}
            <div className="overflow-x-auto overflow-y-hidden pb-0.5">
                <div className={styles.lineChart} style={{ minWidth }}>
                    <canvas ref={canvasRef} />
                </div>
            </div>
            <CardActions>{showLegend ? <Legend legend={legend} onClick={toggleLegend} /> : null}</CardActions>
        </Card>
    );
};

export default LineChart;
